import React, { useState, useEffect, useRef } from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';

import { useAddOptionDialogStyles } from './AddOptionDialog.styles';

const DEBOUNCE_MS = 300;

export interface AddOptionDialogProps {
  open: boolean;
  label: string;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  validate: (val: string) => string | null;
  /**
   * Called with the typed input on submit. Must resolve with the full created
   * item ({ id, value }) so the parent can store both and auto-select it, or
   * reject with an Error whose message is displayed inline.
   */
  // eslint-disable-next-line no-unused-vars
  onAdd: (val: string) => Promise<{ id: number; value: string }>;
  /** Called with the newly created item on success, e.g. to auto-select it in the dropdown. */
  // eslint-disable-next-line no-unused-vars
  onSuccess: (item: { id: number; value: string }) => void;
}

/**
 * Modal dialog for adding a new option to an async-populated dropdown.
 * Manages its own input, validation, loading, and error states internally.
 */
const AddOptionDialog = ({
  open,
  label,
  onClose,
  validate,
  onAdd,
  onSuccess
}: AddOptionDialogProps) => {
  const classes = useAddOptionDialogStyles();

  const [input, setInput] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset all state each time the dialog opens so each session starts fresh
  useEffect(() => {
    if (!open) return;
    setInput('');
    setValidationError(null);
    setSubmitting(false);
    setSubmitError(null);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, [open]);

  // Cancel any pending debounce on unmount
  useEffect(
    () => () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    },
    []
  );

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    const { value: newValue } = event.target;
    setInput(newValue);
    setSubmitError(null);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!newValue) {
      setValidationError(null);
      return;
    }
    debounceTimer.current = setTimeout(() => {
      setValidationError(validate(newValue));
    }, DEBOUNCE_MS);
  };

  const handleSubmit = async () => {
    const err = validate(input);
    if (err) {
      setValidationError(err);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const newItem = await onAdd(input);
      onSuccess(newItem);
      onClose();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to add. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitDisabled = !!validationError || !input || submitting;
  const fieldError = validationError ?? submitError ?? '';

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth='xs' fullWidth>
      <div className={classes.paper}>
        <DialogTitle>Add New {label}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            variant='outlined'
            placeholder={`Enter ${label}`}
            value={input}
            onChange={handleInputChange}
            error={!!validationError || !!submitError}
            helperText={fieldError}
            className={classes.input}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <div className={classes.loadingContainer}>
            {submitting && <CircularProgress size={20} />}
          </div>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            className={classes.submitButton}
            variant='contained'
            onClick={handleSubmit}
            disabled={isSubmitDisabled}>
            {submitting ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default AddOptionDialog;
